import { motion } from "framer-motion";
import { ProgressRing } from "@/components/ui/ProgressRing";

interface TrackerCardProps {
  title: string;
  icon: string;
  progress: number;
  color: string;
  subtitle?: string;
  onClick: () => void;
  isExpanded?: boolean;
  children?: React.ReactNode;
}

export const TrackerCard = ({
  title,
  icon,
  progress,
  color,
  subtitle,
  onClick,
  isExpanded,
  children,
}: TrackerCardProps) => {
  return (
    <motion.div
      className={`bg-card border border-border/50 rounded-lg overflow-hidden transition-shadow ${
        isExpanded ? "shadow-md" : "hover:shadow-sm"
      }`}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ scale: isExpanded ? 1 : 1.02 }}
      transition={{ duration: 0.2 }}
    >
      {/* Card Header - Always visible */}
      <button
        onClick={onClick}
        className="w-full p-4 flex items-center gap-4 text-left hover:bg-accent/30 transition-colors"
      >
        <ProgressRing
          progress={progress}
          size={56}
          strokeWidth={5}
          color={color}
          showLabel={false}
        >
          <span className="text-xl">{icon}</span>
        </ProgressRing>
        
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-foreground text-sm">{title}</h3>
          {subtitle && (
            <p className="text-xs text-muted-foreground mt-0.5 truncate">
              {subtitle}
            </p>
          )}
        </div>
        
        <div className="flex flex-col items-end">
          <span 
            className="text-lg font-semibold"
            style={{ color }}
          >
            {Math.round(progress)}%
          </span>
          <span className="text-xs text-muted-foreground">complete</span>
        </div>
      </button>

      {/* Expandable Content */}
      {isExpanded && children && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="border-t border-border/50"
          onClick={(e) => e.stopPropagation()}
        >
          <div 
            className="p-4 bg-muted/20"
            onClick={(e) => e.stopPropagation()}
          >
            {children}
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};
